import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Pagination,
  Result,
  Row,
  TreeSelect,
  Image,
  Skeleton,
} from "antd";

import moment from "moment";
import { map } from "lodash";
import {
  ClockCircleOutlined,
  DownloadOutlined,
  EyeFilled,
  SearchOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";

import { pdfjsVersion } from '../../../index';
import { Viewer, Worker } from "@react-pdf-viewer/core";

// Plugins
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

// Application components
import service from "../../../services/documentRepoService";
import { recordError } from "../../../utils/logService";
import { openNotification, ActivityIndicator } from "../../custom";
import { Messages, Notify } from "../../../services/custom/Messages";
import { util } from "../../../utils/functions/custom";
import apiLookups from "../../../services/commonLookupService";

export default function FilterControl(handleInputChange) {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const allOptions = "All Options";
  const initWidth = 650;
  const emptyString = "";
  let msg = "";

  const [total, setTotal] = useState(1);
  const [numEachPage, setNumEachPage] = useState(10);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [typeCounts, setTypeCounts] = useState([]);

  const [publicationYears, setPublicationYears] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [categoriesTrata, setCategoriesTrata] = useState([]);

  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [languagesTrata, setLanguagesTrata] = useState([]);

  const [searchText, setSearchText] = useState(emptyString);
  const [isSelected, setIsSelected] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState();
  const [documentList, setDocumentList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [DocumentDetails, setDocumentDetails] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(emptyString);
  const [previewTitle, setPreviewTitle] = useState(emptyString);
  const [toggleFiles, setToggleFiles] = useState(false);
  const [modalWidth, setModalWidth] = useState(initWidth);
  const [physicalFile, setPhysicalFile] = useState({});

  const [isEmptyResults, setIsEmptyResults] = useState(false);
  const [isNotSearchable, setIsNotSearchable] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [progressMessage, setProgressMessage] = useState(
    "File is being uploaded, please wait..."
  );

  const [minValue, setMinValue] = useState(0);

  useEffect(() => {
    reIndexSearchDocuments();
    getDropdownOptions();

    window.addEventListener("resize", () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < initWidth) {
        setModalWidth(screenWidth * 0.8);
      }
    });
  }, [window]);

  const getDropdownOptions = async () => {
    getDocumentTypes();
    getDocumentCategories();
    getLanguages();
  };

  const getRepoList = async (types = documentTypes) => {
    try {
      const response = await service.getRepoDocuments(-1);

      if (response && response.status === 200) {
        const { data } = response;
        const sorted = data.sort((a, b) =>
          b.approvalDate.localeCompare(a.approvalDate)
        );
        setDocumentList(sorted);
        setFilteredData(sorted);
        mapPublicationYears(data);
        setTotal(data.length);
        countDocumentTypes({ types: types, resultsData: data });
        setIsEmptyResults(data.length === 0);
      } else {
        setDocumentList([]);
        setFilteredData([]);
        setTotal(0);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const handleMoreDetails = async (e, id) => {
    try {
      if (id) {
        const response = await service.searchDocumentDetails(id);

        if (response && response.status === 200) {
          const { data } = response;
          setDocumentDetails(data);
        } else {
          setDocumentDetails({});
        }
      }
      setIsSelected(true);
      setSelectedDocId(id);
      e.preventDefault();
    } catch (error) {
      recordError(error);
    }
  };

  const reIndexSearchDocuments = async () => {
    try {
      const response = await service.reIndexSearchDocs();
      if (response && response.status === 200) {
      } else {
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getDocumentTypes = async () => {
    try {
      const response = await service.getDocumentTypes();
      if (response && response.status === 200) {
        const { data } = response;
        const sorted = data.sort((a, b) => a.value.localeCompare(b.value));
        const treeData = mapKeValueToTree(sorted);
        setDocumentTypes(sorted);
        getRepoList(sorted);
      } else {
        setDocumentTypes([]);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getDocumentCategories = async () => {
    try {
      const response = await service.getDocumentCategories();

      if (response && response.status === 200) {
        const { data } = response;
        const sorted = data.sort((a, b) => a.value.localeCompare(b.value));
        setDocumentCategories(sorted);
        const treeData = mapKeValueToTree(sorted);
        setCategoriesTrata(treeData);
      } else {
        setDocumentCategories([]);
        setCategoriesTrata([]);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getLanguages = async () => {
    try {
      const response = await apiLookups.getLanguges();
      if (response && response.status === 200) {
        const { data } = response;
        const sorted = data.sort((a, b) => a.value.localeCompare(b.value));
        setLanguages(sorted);
        const treeData = mapKeValueToTree(sorted);
        setLanguagesTrata(treeData);
      } else {
        setLanguages([]);
        setLanguagesTrata([]);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const countDocumentTypes = ({ types = documentTypes, resultsData = [] }) => {
    const counts = types.map((item) => {
      const count = resultsData.filter(
        (d) => d.publicationType === item.value
      ).length;
      return {
        value: item.value,
        title: item.value + " (" + count + ")",
      };
    });
    setTypeCounts(counts);
  };

  const mapPublicationYears = (data) => {
    const years = data.map((item) => {
      return moment(item.approvalDate, "YYYY-MM-DD HH:mm:ss").year();
    });

    var yearsTreeData = Array.from(new Set(years))
      .sort()
      .reverse()
      .map((item) => {
        return { title: item === 1 ? "No Date" : item, value: item };
      });
    // sorted.unshift(allOptions);
    setPublicationYears(yearsTreeData);
  };

  const mapKeValueToTree = (listData) => {
    const treeData = listData.map((item) => {
      return {
        title: item.value,
        value: item.value,
        // value: item.key ? item.key : item.value
      };
    });
    return treeData;
  };

  const addArrayToQuery = (_query, _element, arrayaData, lookupArray = []) => {
    var start = _query.length > 0 ? "&" : "";
    arrayaData.forEach((item) => {
      const value = lookupArray.find((l) => l.value === item)?.key || item;
      _query += start + _element + "=" + value;
      start = "&";
    });
    return _query;
  };

  const handleSearchRequest = async ({ searchValue = searchText }) => {
    try {
      let _filteredData = [];
      const hasSearchText = searchValue.length > 0;
      const noFilters =
        selectedTypes.length === 0 &&
        selectedCategories.length === 0 &&
        selectedYears.length === 0 &&
        selectedLanguages.length === 0;

      let queryData = hasSearchText ? "text=" + searchValue : "";
      queryData = addArrayToQuery(
        queryData,
        "types",
        selectedTypes,
        documentTypes
      );
      queryData = addArrayToQuery(
        queryData,
        "categories",
        selectedCategories,
        documentCategories
      );
      queryData = addArrayToQuery(
        queryData,
        "years",
        selectedYears,
        publicationYears
      );
      queryData = addArrayToQuery(
        queryData,
        "languages",
        selectedLanguages,
        languages
      );

      const response =
        !hasSearchText && noFilters
          ? await service.getRepoDocuments(-1)
          : await service.searchDocument(queryData);
      if (response && response.status === 200) {
        if (hasSearchText) {

          const { data } = response;
          _filteredData = data;
          // _filteredData = result?.sort((a, b) =>
          //   b.approvalDate.localeCompare(a.approvalDate)
          // );
        }
        else {
          const { data } = response;
          _filteredData = data?.data?.sort((a, b) =>
            b.approvalDate.localeCompare(a.approvalDate)
          );

        }

        setDocumentList(_filteredData);
        setFilteredData(_filteredData);
        countDocumentTypes({ resultsData: _filteredData });
        setTotal(_filteredData.length);
        setMinValue(0);
        
        const stillSelected = _filteredData.find(
          (record) => record.id === isSelected
        )
          ? true
          : false;
        setIsSelected(stillSelected);
        setIsEmptyResults(_filteredData.length === 0);
      }

      // if (true) {
      //   setDocumentList(_filteredData);
      //   setFilteredData(_filteredData);
      //   countDocumentTypes({ resultsData: _filteredData });
      //   setTotal(_filteredData.length);
      //   const stillSelected = _filteredData.find(
      //     (record) => record.id === isSelected
      //   )
      //     ? true
      //     : false;
      //   setIsSelected(stillSelected);
      //   setIsEmptyResults(_filteredData.length === 0);
      // } else {
      //   aggregateFiltering({ overrideDataList: true });
      // }
    } catch (error) {
      console.log("ERROR ON SEARCH RESULTS", error);
    }
  };

  const aggregateFiltering = async ({
    filteredTypes = selectedTypes,
    filteredCategories = selectedCategories,
    filteredYears = selectedYears,
    filteredLanguages = selectedLanguages,
    overrideDataList = false,
  }) => {
    try {
      var _filteredData = map(documentList, (record) => {
        const typeMatch =
          filteredTypes.length > 0
            ? filteredTypes.includes(record.publicationType)
            : true;
        const categoryMatch =
          filteredCategories.length > 0
            ? filteredCategories.includes(record.category)
            : true;
        const yearMatch =
          filteredYears.length > 0
            ? filteredYears.includes(
                moment(record.approvalDate, "YYYY-MM-DD HH:mm:ss").year()
              )
            : true;
        const languageMatch =
          filteredLanguages.length > 0
            ? record.languages.some((lan) =>
                filteredLanguages.includes(lan.value)
              )
            : true;

        const match = typeMatch && categoryMatch && languageMatch && yearMatch;
        return match ? record : null;
      }).filter((record) => record);
      setTotal(_filteredData.length);
      // countDocumentTypes({ resultsData: _filteredData });
      setIsEmptyResults(_filteredData.length === 0);
      setFilteredData(_filteredData);
      const stillSelected = _filteredData.find(
        (record) => record.id === isSelected
      )
        ? true
        : false;
      setIsSelected(stillSelected);
      setIsEmptyResults(_filteredData.length === 0);
      if (overrideDataList) {
        setDocumentList(_filteredData);
      }
    } catch (error) {
      msg = "Failed to apply filters";
      openNotification("Document Search", msg, Notify.ERROR);
      console.log("ERROR ON SEARCH RESULTS", error);
    }
  };

  const toggleSearchButton = ({
    _types = selectedTypes,
    _categories = selectedCategories,
    _languages = selectedLanguages,
    _years = selectedYears,
    _text = searchText,
  }) => {
    const canSearch =
      _types?.length > 0 ||
      _categories?.length > 0 ||
      _languages?.length > 0 ||
      _years?.length > 0 ||
      _text?.length > 0;

    setIsNotSearchable(!canSearch);
  };

  const arrayFilter = (searchList, reg) => {
    var isMatching = null;
    for (let index = 0; index < searchList.length; index++) {
      const item = searchList[index];
      isMatching = item.toLowerCase().match(reg);
      if (isMatching != null) {
        break;
      }
    }
    return isMatching;
  };

  const handleGlobalSearch = (e) => {
    const { value: text } = e.target;
    setSearchText(text);
    toggleSearchButton({ _text: text });
    if (text === "") {
      getRepoList();
    }
  };

  const handleTypeChange = (types) => {
    setSelectedTypes(types);
    toggleSearchButton({ _types: types });
    aggregateFiltering({ filteredTypes: types });
  };

  const handleCategoryChange = (categories) => {
    setSelectedCategories(categories);
    toggleSearchButton({ _categories: categories });
    aggregateFiltering({ filteredCategories: categories });
  };

  const handleYearChange = (years) => {
    setSelectedYears(years);
    toggleSearchButton({ _years: years });
    aggregateFiltering({ filteredYears: years });
  };

  const handleLanguageChange = (langs) => {
    setSelectedLanguages(langs);
    toggleSearchButton({ _languages: langs });
    aggregateFiltering({ filteredLanguages: langs });
  };

  const clearFilters = (e) => {
    e.preventDefault();
    setSelectedYears([]);
    setSelectedTypes([]);
    setSelectedCategories([]);
    setSelectedLanguages([]);
    setSearchText(emptyString);
    setIsNotSearchable(true);
    getRepoList();
  };

  const backHome = (e) => {
    setIsSelected(false);
  };

  const handleCancel = () => setPreviewOpen(false);

  const handlePreviewDocument = async (document) => {
    try {
      setProgressMessage("File is being downloaded, please wait...");
      setSubmitted(true);
      const response = await service.searchDownloadFile(document.id);
      if (response) {
        setSubmitted(false);
        const { data } = response;
        const new_blob = new Blob([data]);
        const url = URL.createObjectURL(new_blob);

        setPreviewImage(url);
        setPreviewOpen(true);
        setPreviewTitle(document.filename);
        setToggleFiles(document.type !== "application/pdf");
      }
    } catch (error) {
      recordError(error);
    }
  };

  const handleDownloadDocument = async (id, filename) => {
    try {
      setProgressMessage("File is being downloaded, please wait...");
      setSubmitted(true);
      const response = await service.searchDownloadFile(id);
      if (response) {
        setSubmitted(false);
        const { data } = response;
        const new_blob = new Blob([data]);

        const url = URL.createObjectURL(new_blob);
        const downloadLink = document.createElement("a");

        downloadLink.href = url;
        downloadLink.download = filename;
        downloadLink.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      msg = util.parseMessage(Messages.FILEDOWNLOAD_FAILURE, "File");
      openNotification("File Download", msg, Notify.ERROR);
      console.log("PHYSICAL FILE", error);
      recordError(error);
    }
  };

  const withAllSelector = (selectedValues, setSelectedValues, lookupValues) => {
    const allIds = lookupValues.map(({ value }) => value);

    return [
      {
        title:
          selectedValues.length === lookupValues.length ? (
            <span
              onClick={() => setSelectedValues([])}
              style={{
                display: "inline-block",
                color: "#286FBE",
                cursor: "pointer",
              }}
            >
              Unselect all
            </span>
          ) : (
            <span
              onClick={() => setSelectedValues(allIds)}
              style={{
                display: "inline-block",
                color: "#286FBE",
                cursor: "pointer",
              }}
            >
              Select all
            </span>
          ),
        value: "all-option",
        disableCheckbox: true,
        // disabled: true
      },
      ...lookupValues,
    ];
  };

  const tProps = {
    // value,
    showSearch: true,
    treeCheckable: true,
    showCheckedStrategy: TreeSelect.SHOW_CHILD,
    style: {
      width: "100%",
    },
  };

  const onPageChange = (page, pageSize) => {
    setMinValue((page - 1) * numEachPage);
  };
  const onShowSizeChange = (current, size) => {
    setNumEachPage(size);
  };
  const backPress = (event) => {
  };
  return (
    <>
      {/* <Form layout="vertical"> */}
      {/* Previous Color used for side by side div's: #5DADE2 */}
      <Card className="doc-repo-card repo-search" bordered={false}>
        <Row className="doc-repo-search">
          {/* <div key={"serach-section"} > */}
          <Col className="filter-control">
            <span style={{ fontSize: "15px", color: "#000" }}>
              <b>Search Terms</b>&nbsp;<i>(space separated)</i>
            </span>
            <Input
              placeholder="Description Keywords Title Authors Affiliated Organisations Funding Agencies"
              type="text"
              className="search-bar"
              onChange={handleGlobalSearch}
              onPaste={handleGlobalSearch}
              onPressEnter={handleSearchRequest}
              value={searchText}
              allowClear
              size="large"
              prefix={<SearchOutlined />}
              name=""
            />
          </Col>

          <Col className="filter-button">
            <span></span> <br />
            <Button
              disabled={isNotSearchable}
              key="Search"
              value="Search"
              type="primary"
              onClick={handleSearchRequest}
              style={{ marginLeft: 10 }}
            >
              Search
            </Button>
          </Col>
        </Row>
        <br></br>
        {/* <div class="divider"></div> */}
        <Row className="filter-selections">
          {/* DOTO: Design reusable component to minimise code duplication */}
          <Col xs={24} style={{ marginLeft: 10, marginBottom: 0 }}>
            <h3
              style={{ marginBottom: 0, fontWeight: "bold", color: "#f1f1f1" }}
            >
              Filters
            </h3>
          </Col>
          <Col className="filter-control" key={"f-types"}>
            <span style={{ fontSize: "15px", color: "white" }}>
              <b>Document Types</b>
            </span>
            <TreeSelect
              onChange={handleTypeChange}
              value={selectedTypes}
              treeData={withAllSelector(
                selectedTypes,
                setSelectedTypes,
                typeCounts
              )}
              placeholder={"Filter by type"}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+ ${omittedValues.length} ... Others`
              }
              {...tProps}
            />
          </Col>
          <Col
            className="filter-control"
            style={{ marginLeft: 10 }}
            key={"f-categories"}
          >
            <span style={{ fontSize: "15px", color: "white" }}>
              <b>Document Categories</b>
            </span>
            <TreeSelect
              onChange={handleCategoryChange}
              value={selectedCategories}
              treeData={withAllSelector(
                selectedCategories,
                setSelectedCategories,
                categoriesTrata
              )}
              placeholder={"Filter by category"}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+ ${omittedValues.length} Others..`
              }
              {...tProps}
            />
          </Col>
          <Col
            className="filter-control"
            style={{ marginLeft: 10 }}
            key={"f-years"}
          >
            <span style={{ fontSize: "15px", color: "white" }}>
              <b>Publication Years</b>
            </span>
            <br />
            <TreeSelect
              onChange={handleYearChange}
              value={selectedYears}
              treeData={withAllSelector(
                selectedYears,
                setSelectedYears,
                publicationYears
              )}
              placeholder={"Filter by year"}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+ ${omittedValues.length} Others...`
              }
              {...tProps}
            />
          </Col>
          <Col
            className="filter-control"
            style={{ marginLeft: 10 }}
            key={"f-languages"}
          >
            <span style={{ fontSize: "15px", color: "white" }}>
              <b>Languages</b>
            </span>
            <br />
            <TreeSelect
              onChange={handleLanguageChange}
              value={selectedLanguages}
              treeData={withAllSelector(
                selectedLanguages,
                setSelectedLanguages,
                languagesTrata
              )}
              placeholder={"Filter by language"}
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+ ${omittedValues.length} Others...`
              }
              {...tProps}
            />
          </Col>
          <Col
            className="filter-button"
            style={{
              marginTop: "25px",
              paddingTop: "12px",
              paddingBottom: "12px",
            }}
          >
            <a
              href="#"
              className="applyFilter"
              onClick={(e) => {
                clearFilters(e);
              }}
            >
              Clear Filters
            </a>
          </Col>
        </Row>
      </Card>
      <div className="pagination">
        {!isEmptyResults && (
          <Pagination
            defaultCurrent={1}
            defaultPageSize={numEachPage}
            onChange={onPageChange}
            total={total}
            showSizeChanger
            onShowSizeChange={onShowSizeChange}
            pageSizeOptions={[2, 5, 10, 20, 50, 100]}
            pageSize={numEachPage}
            showQuickJumper
            showTotal={(total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} results`
            }
          />
        )}
      </div>
      <div className="parent">
        {isEmptyResults ? (
          <Card className="doc-repo-card-empty" bordered={false}>
            <div className="no-results center-align">
              <Image
                style={{}}
                className=""
                src={require("../../../assets/no-results.jpg")}
                alt="Results not found image"
                preview={false}
              />
              <Result title="Sorry,we couldn't find any results, please try again using a different search criteria." />
            </div>
          </Card>
        ) : (
          <Card className="doc-repo-card" bordered={false}>
            <div className="child1" key={"child1"}>
              <div>
                <br></br>
                <div className="divider"></div>

                {filteredData && filteredData?.length === 0 ? (
                  <Skeleton active={true} />
                ) : (
                  filteredData?.length > 0 &&
                  filteredData
                    .slice(minValue, minValue + numEachPage)
                    .map((doc) => {
                      return (
                        <>
                          <div
                            className={
                              doc.id === selectedDocId
                                ? "doc-search-list selected"
                                : "doc-search-list"
                            }
                            key={doc.id}
                          >
                            <div
                              className="doc-title"
                              style={{ display: "flex" }}
                            >
                              <div
                                className="t"
                                style={{
                                  color: "##4B4B4B",
                                  overflow: "hidden",
                                }}
                              >
                                <b>{doc.title}</b>
                              </div>
                              <div
                                style={{ marginLeft: "auto", color: "#17202A" }}
                              ></div>
                            </div>
                            <div style={{ display: "flex" }}>
                              <div style={{ color: "#17202A" }}>
                                <UsergroupAddOutlined />{" "}
                                {"Author(s) : " +
                                  doc.authors?.toString().replaceAll(",", ", ")}
                              </div>
                              <div
                                style={{ marginLeft: "auto", color: "#17202A" }}
                              >
                                <ClockCircleOutlined /> &nbsp;
                                {doc.approvalDate !== "0001-01-01T00:00:00"
                                  ? moment(
                                      doc.approvalDate,
                                      "YYYY-MM-DD"
                                    ).format("DD MMM YYYY")
                                  : "No Date"}
                                <br></br>
                                <br></br>
                              </div>
                            </div>
                            <div className="p">{doc.description}</div>
                            <br />
                            <br />
                            <a
                              key={doc.id}
                              onClick={(e) => handleMoreDetails(e, doc.id)}
                            >
                              <b>See details</b>
                            </a>  
                          </div>
                          <br />
                          <div className="divider"></div>
                        </>
                      );
                    })
                )}
              </div>
            </div>
          </Card>
        )}
        {isSelected && (
          <Card className="doc-repo-card-selected" bordered={false}>
            <div className="child2" key={"child2"}>
              <div>
                <>
                  <div className="doc-title" style={{ display: "flex" }}>
                    <div style={{ color: "#17202A" }}>
                      {/* <b>{"Title : " + DocumentDetails.title}</b> */}
                      <b>{DocumentDetails.title}</b>
                    </div>
                  </div>
                  <br></br>
                  <Button
                    style={{ color: "#5DADE2" }}
                    type="default"
                    key={"doc-btn-" + DocumentDetails.id}
                    onClick={(e) => handlePreviewDocument(DocumentDetails)}
                  >
                    <EyeFilled className="teamSocialIcon" />
                    Preview
                  </Button>
                  <Button
                    style={{ color: "#0000FF", marginLeft: 10 }}
                    type="primary"
                    key={"doc-btn1" + DocumentDetails.id}
                    onClick={(e) =>
                      handleDownloadDocument(
                        DocumentDetails?.id,
                        DocumentDetails?.filename
                      )
                    }
                  >
                    <DownloadOutlined className="teamSocialIcon" />
                    Download
                  </Button>
                  {submitted === true && (
                    <ActivityIndicator
                      visible={true}
                      message={progressMessage}
                    />
                  )}
                  <br></br>
                  <br></br>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <div
                      style={{
                        color: "#17202A",
                        display: "flex",
                        flexDirection: "row",
                        flex: "2 1 0px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", flex: "1 1 0px" }}>
                        Author(s):
                      </div>
                      <div style={{ flex: "3 1 0px" }}>
                        {DocumentDetails.authors
                          ?.toString()
                          .replaceAll(",", ", ")}
                      </div>
                    </div>

                    <div
                      style={{
                        color: "#17202A",
                        display: "flex",
                        flexDirection: "row",
                        flex: "1 1 0px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", flex: "1 1 0px" }}>
                        Type:
                      </div>
                      <div style={{ flex: "2 1 0px" }}>
                        {DocumentDetails.publicationType}
                      </div>
                    </div>
                  </div>

                  <br></br>

                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <div
                      style={{
                        color: "#17202A",
                        display: "flex",
                        flexDirection: "row",
                        flex: "2 1 0px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", flex: "1 1 0px" }}>
                        Date:
                      </div>
                      <div style={{ flex: "3 1 0px" }}>
                        <ClockCircleOutlined /> &nbsp;
                        {DocumentDetails.approvalDate !== "0001-01-01T00:00:00"
                          ? moment(
                              DocumentDetails.approvalDate,
                              "YYYY-MM-DD"
                            ).format("DD MMM YYYY")
                          : "No Date"}
                      </div>
                    </div>

                    <div
                      style={{
                        color: "#17202A",
                        display: "flex",
                        flexDirection: "row",
                        flex: "1 1 0px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", flex: "1 1 0px" }}>
                        Category:
                      </div>
                      <div style={{ flex: "2 1 0px" }}>
                        {DocumentDetails.category}
                      </div>
                    </div>
                  </div>

                  <br></br>

                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <div
                      style={{
                        color: "#17202A",
                        display: "flex",
                        flexDirection: "row",
                        flex: "2 1 0px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", flex: "1 1 0px" }}>
                        Affiliated Organisation(s):
                      </div>
                      <div style={{ flex: "3 1 0px" }}>
                        {DocumentDetails.organisations
                          ?.map((item) => item.name)
                          .join(", ")}
                      </div>
                    </div>

                    <div
                      style={{
                        color: "#17202A",
                        display: "flex",
                        flexDirection: "row",
                        flex: "1 1 0px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", flex: "1 1 0px" }}>
                        Language(s):
                      </div>
                      <div style={{ flex: "2 1 0px" }}>
                        {DocumentDetails.languages
                          ?.map((item) => item.value)
                          .join(", ")}
                      </div>
                    </div>
                  </div>

                  <br></br>

                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <div
                      style={{
                        color: "#17202A",
                        display: "flex",
                        flexDirection: "row",
                        flex: "2 1 0px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", flex: "1 1 0px" }}>
                        Funding Source(s):
                      </div>
                      <div style={{ flex: "3 1 0px" }}>
                        {DocumentDetails.fundinAgencies
                          ?.map((item) => item.name)
                          .join(", ")}
                      </div>
                    </div>
                    <div
                      style={{
                        color: "#17202A",
                        display: "flex",
                        flexDirection: "row",
                        flex: "1 1 0px",
                      }}
                    ></div>
                  </div>

                  <br />
                  <br />
                  <div className="divider"></div>
                  {DocumentDetails.description}
                  <br />
                  <br />

                  <br />
                </>
                <Button
                  key="3b"
                  type="defaut"
                  className="right-align-button"
                  onClick={backHome}
                >
                  Back
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
      <div className="pagination">
        {!isEmptyResults && (
          <Pagination
            defaultCurrent={1}
            defaultPageSize={numEachPage}
            onChange={onPageChange}
            total={total}
            showSizeChanger
            onShowSizeChange={onShowSizeChange}
            pageSizeOptions={[2, 5, 10, 20, 50, 100]}
            pageSize={numEachPage}
            showQuickJumper
            showTotal={(total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} results`
            }
          />
        )}
      </div>
      <Modal
        key={"modal-preview"}
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
        width={modalWidth}
      >
        {/* {toggleFiles ? (
                    <img
                        alt="exampleeeee"
                        style={{
                            width: "100%",
                        }}
                        src={previewImage}
                    />
                ) : ( */}
        <Worker
          workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion()}/build/pdf.worker.min.js`}
        >
          <Viewer
            fileUrl={previewImage}
            plugins={[defaultLayoutPluginInstance]}
            renderError={console.log}
          />
        </Worker>
      </Modal>
      {/* </Form> */}
    </>
  );
}
